import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type { ReactNode } from 'react';
import { money, moneyCompact } from '../../../lib/format';
import { formatValue, useSeriesColors, type ValueKind } from './chartTheme';
import { useShop } from '../../../shop/ShopProvider';

const axisCommon = {
  tickLabelStyle: { fontSize: 11 },
  disableLine: true,
  disableTicks: true,
} as const;

const chartSx = {
  '& .MuiLineChart-area': { fillOpacity: 0.12 },
  '& .MuiLineChart-line': { strokeWidth: 2.25 },
  '& .MuiChartsGrid-line': { strokeDasharray: '4 4', opacity: 0.5 },
  '& .MuiChartsAxis-tickLabel': { fill: 'var(--mui-palette-text-secondary)' },
};

export interface TrendSeries {
  key: string;
  label: string;
  data: number[];
  /** Use the brand colour (one primary series) instead of a categorical slot. */
  brand?: boolean;
  area?: boolean;
  dashed?: boolean;
}

/** Line/area trend over labelled periods. */
export function TrendChart({
  labels,
  series,
  height = 300,
  loading,
  kind = 'money',
}: {
  labels: string[];
  series: TrendSeries[];
  height?: number;
  loading?: boolean;
  kind?: ValueKind;
}) {
  const { format } = useShop();
  const { colors } = useSeriesColors();
  if (loading) return <Skeleton variant="rounded" height={height} />;
  if (labels.length === 0) return <ChartEmpty height={height} />;
  const fmtAxis = (v: number | null) => formatValue(v, kind, format, true);
  const fmtValue = (v: number | null) => formatValue(v, kind, format);
  const trendSx: Record<string, unknown> = { ...chartSx };
  for (const s of series)
    if (s.dashed)
      trendSx[`& .MuiLineChart-line[data-series="${s.key}"]`] = { strokeDasharray: '5 4' };
  let slot = 0;
  return (
    <Box sx={{ height, mx: -1 }}>
      <LineChart
        height={height}
        margin={{ left: 4, right: 16, top: 16, bottom: 4 }}
        grid={{ horizontal: true }}
        hideLegend={series.length < 2}
        slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
        xAxis={[{ scaleType: 'point', data: labels, ...axisCommon }]}
        yAxis={[{ valueFormatter: fmtAxis, width: 68, ...axisCommon }]}
        series={series.map((s) => ({
          id: s.key,
          label: s.label,
          data: s.data,
          area: s.area ?? false,
          showMark: labels.length <= 14,
          curve: 'monotoneX' as const,
          color: s.brand
            ? 'var(--mui-palette-primary-main)'
            : (colors[slot++ % colors.length] ?? '#2a78d6'),
          valueFormatter: fmtValue,
        }))}
        sx={trendSx}
      />
    </Box>
  );
}

/** Vertical bars per period (one or more series, side by side). */
export function PeriodBars({
  labels,
  series,
  height = 300,
  loading,
  kind = 'money',
  stacked,
}: {
  labels: string[];
  series: { key: string; label: string; data: number[]; brand?: boolean; color?: string }[];
  height?: number;
  loading?: boolean;
  kind?: ValueKind;
  stacked?: boolean;
}) {
  const { format } = useShop();
  const { colors } = useSeriesColors();
  if (loading) return <Skeleton variant="rounded" height={height} />;
  if (labels.length === 0) return <ChartEmpty height={height} />;
  const fmtAxis = (v: number | null) => formatValue(v, kind, format, true);
  const fmtValue = (v: number | null) => formatValue(v, kind, format);
  let slot = 0;
  return (
    <Box sx={{ height, mx: -1 }}>
      <BarChart
        height={height}
        margin={{ left: 4, right: 16, top: 16, bottom: 4 }}
        grid={{ horizontal: true }}
        borderRadius={4}
        hideLegend={series.length < 2}
        slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
        xAxis={[
          {
            scaleType: 'band',
            data: labels,
            categoryGapRatio: 0.35,
            barGapRatio: 0.15,
            ...axisCommon,
          },
        ]}
        yAxis={[{ valueFormatter: fmtAxis, width: 68, ...axisCommon }]}
        series={series.map((s) => ({
          id: s.key,
          label: s.label,
          data: s.data,
          stack: stacked ? 'total' : undefined,
          color:
            s.color ??
            (s.brand
              ? 'var(--mui-palette-primary-main)'
              : (colors[slot++ % colors.length] ?? '#2a78d6')),
          valueFormatter: fmtValue,
        }))}
        sx={chartSx}
      />
    </Box>
  );
}

/** Horizontal ranking bars (top customers, products, suppliers). */
export function RankingBars({
  items,
  loading,
  kind = 'money',
  label = 'Total',
  max = 8,
}: {
  items: { label: string; value: number }[];
  loading?: boolean;
  kind?: ValueKind;
  label?: string;
  max?: number;
}) {
  const { format } = useShop();
  const top = items.slice(0, max);
  const height = Math.max(160, top.length * 38 + 32);
  if (loading) return <Skeleton variant="rounded" height={260} />;
  if (top.length === 0) return <ChartEmpty height={200} />;
  const fmt = (v: number | null) => formatValue(v, kind, format);
  return (
    <Box sx={{ height, mx: -1 }}>
      <BarChart
        layout="horizontal"
        height={height}
        margin={{ left: 4, right: 24, top: 8, bottom: 4 }}
        borderRadius={4}
        hideLegend
        grid={{ vertical: true }}
        yAxis={[
          {
            scaleType: 'band',
            data: top.map((t) => (t.label.length > 22 ? `${t.label.slice(0, 21)}…` : t.label)),
            width: 150,
            categoryGapRatio: 0.38,
            ...axisCommon,
          },
        ]}
        xAxis={[
          {
            valueFormatter: (v: number | null) => formatValue(v, kind, format, true),
            ...axisCommon,
          },
        ]}
        series={[
          {
            id: 'value',
            label,
            data: top.map((t) => t.value),
            color: 'var(--mui-palette-primary-main)',
            valueFormatter: fmt,
          },
        ]}
        sx={chartSx}
      />
    </Box>
  );
}

/**
 * Donut with a centre total and a legend list. More than `maxSlices` items fold into "Other" (a
 * categorical palette never cycles).
 */
export function Donut({
  items,
  loading,
  centerLabel,
  centerValue,
  maxSlices = 5,
  height = 220,
}: {
  items: { id: string; label: string; value: number }[];
  loading?: boolean;
  centerLabel?: string;
  centerValue?: ReactNode;
  maxSlices?: number;
  height?: number;
}) {
  const { format } = useShop();
  const { colors, other } = useSeriesColors();
  if (loading)
    return (
      <Skeleton variant="circular" width={height - 20} height={height - 20} sx={{ mx: 'auto' }} />
    );
  const positive = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  if (positive.length === 0) return <ChartEmpty height={height} />;
  const head = positive.slice(0, maxSlices);
  const rest = positive.slice(maxSlices);
  const slices = [
    ...head.map((h, i) => ({ ...h, color: colors[i] ?? other })),
    ...(rest.length
      ? [
          {
            id: 'other',
            label: 'Other',
            value: rest.reduce((s, r) => s + r.value, 0),
            color: other,
          },
        ]
      : []),
  ];
  const total = slices.reduce((s, x) => s + x.value, 0);
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} sx={{ alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: height, height, flexShrink: 0 }}>
        <PieChart
          width={height}
          height={height}
          hideLegend
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          series={[
            {
              data: slices.map((s) => ({
                id: s.id,
                label: s.label,
                value: s.value,
                color: s.color,
              })),
              innerRadius: height * 0.31,
              outerRadius: height * 0.47,
              paddingAngle: 1.5,
              cornerRadius: 4,
              valueFormatter: (v) => money(v.value, format),
            },
          ]}
        />
        {(centerLabel ?? centerValue) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'none',
              textAlign: 'center',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">
                {centerLabel}
              </Typography>
              <Typography variant="h6" className="tabular">
                {centerValue ?? moneyCompact(total, format)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Stack spacing={1.1} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {slices.map((s) => (
          <Stack key={s.id} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <Box
              sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color, flexShrink: 0 }}
            />
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {s.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" className="tabular">
              {total > 0 ? `${((s.value / total) * 100).toFixed(0)}%` : '—'}
            </Typography>
            <Typography
              variant="body2"
              className="tabular"
              sx={{ fontWeight: 600, minWidth: 90, textAlign: 'right' }}
            >
              {money(s.value, format)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function ChartEmpty({ height }: { height: number }) {
  return (
    <Box
      sx={{
        height,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '12px',
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'divider',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2">No data for this period</Typography>
    </Box>
  );
}

/** Stacked horizontal bar of shares (aging buckets, balance-sheet composition). */
export function ShareBar({ parts }: { parts: { label: string; value: number; color: string }[] }) {
  const { format } = useShop();
  const total = parts.reduce((s, p) => s + Math.max(0, p.value), 0);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          height: 12,
          borderRadius: '6px',
          overflow: 'hidden',
          gap: '2px',
          bgcolor: 'action.hover',
        }}
      >
        {total > 0 &&
          parts
            .filter((p) => p.value > 0)
            .map((p) => (
              <Box
                key={p.label}
                title={`${p.label}: ${money(p.value, format)}`}
                sx={{ flex: p.value, bgcolor: p.color }}
              />
            ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${String(parts.length)}, 1fr)` },
          gap: 1.5,
          mt: 1.75,
        }}
      >
        {parts.map((p) => (
          <Box key={p.label} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: p.color }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {p.label}
              </Typography>
            </Stack>
            <Typography variant="body2" className="tabular" sx={{ fontWeight: 650, mt: 0.25 }}>
              {moneyCompact(p.value, format)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
